/**
 * Timezone-safe datetime formatting for API responses.
 *
 * The FastAPI backend historically stores timestamps via `datetime.utcnow()` —
 * a NAIVE datetime with no tzinfo. FastAPI serializes those as ISO strings
 * WITHOUT a `Z` suffix (e.g. `"2026-07-19T09:00:00"`). Passing that to
 * `new Date(...)` in the browser causes JavaScript to interpret the string
 * as *local time* — which for an IST user displays events 5.5 hours earlier
 * than they actually occurred.
 *
 * This helper guarantees the datetime is treated as UTC:
 *   • strings ending in `Z` or `±HH:MM` → parsed as-is
 *   • naive strings (no offset)          → treated as UTC (`Z` appended)
 *   • already a Date object              → passed through
 *
 * The returned Date is then rendered via `toLocaleString` in the browser's
 * local TZ, which is the correct behaviour.
 */
export function parseUtc(input) {
  if (input == null) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  // ISO with offset already (Z or +HH:MM / -HH:MM at the end of the time part)
  const hasOffset = /(Z|[+\-]\d{2}:?\d{2})$/.test(s);
  return new Date(hasOffset ? s : s + 'Z');
}

/**
 * Format a UTC-stored timestamp as local-TZ 'Sep 15, 2:30 pm' (India-friendly).
 * Returns the fallback (default '—') if the input is empty / invalid.
 */
export function formatUtcDatetime(input, fallback = '—') {
  const d = parseUtc(input);
  if (!d || isNaN(d.getTime())) return fallback;
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Same as above but returns date only. */
export function formatUtcDate(input, fallback = '—') {
  const d = parseUtc(input);
  if (!d || isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
}
